import { ApolloClient, HttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { ApolloProvider } from '@apollo/client/react';
import {
  InteractionRequiredAuthError,
  InteractionStatus,
} from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { FC, ReactNode, useMemo } from 'react';

import { loginRequest } from '../../auth/authConfig';
import { logger } from '../../utils/logger';

interface IApolloProviderWithClientProps {
  children: ReactNode;
}

const ApolloProviderWithClient: FC<IApolloProviderWithClientProps> = ({
  children,
}) => {
  const { instance, inProgress, accounts } = useMsal();
  const testApiKey = import.meta.env.VITE_TEST_API_KEY;

  // Track if authentication is ready
  const isAuthReady =
    inProgress === InteractionStatus.None && accounts.length > 0;

  // Create Apollo Client - recreate when auth state becomes ready
  const client = useMemo(() => {
    logger.debug('🔄 Creating Apollo Client, auth ready:', isAuthReady);

    // Create HTTP link
    const httpLink = new HttpLink({
      uri: import.meta.env.VITE_API_ENDPOINT,
    });

    logger.debug(
      '🔗 Apollo Client connecting to:',
      import.meta.env.VITE_API_ENDPOINT
    );

    // Create auth link that adds the authorization header
    const authLink = setContext(async (_, { headers }) => {
      if (testApiKey) {
        return {
          headers: {
            ...headers,
            'x-api-key': testApiKey,
          },
        };
      }

      if (inProgress !== InteractionStatus.None) {
        logger.debug(
          '⏳ Apollo: MSAL interaction in progress, waiting...',
          inProgress
        );
        // Return empty auth header - query will proceed unauthenticated;
        // the active interaction (login/redirect) will handle re-auth.
        return { headers };
      }

      const account = instance.getActiveAccount() ?? accounts[0];
      if (account !== undefined) {
        try {
          const result = await instance.acquireTokenSilent({
            ...loginRequest,
            account,
          });
          return {
            headers: {
              ...headers,
              authorization: `Bearer ${result.accessToken}`,
            },
          };
        } catch (err) {
          if (err instanceof InteractionRequiredAuthError) {
            // Do not call loginRedirect from per-operation Apollo auth code.
            // Multiple concurrent GraphQL operations can all fail the same silent
            // token request and race each other into MSAL's interactive APIs.
            // main.tsx listens for MSAL's failure event and serializes recovery.
            logger.warn(
              '⚠️ Apollo: silent token acquisition failed, re-auth will be handled by MSAL event listener.',
              err.message
            );
          }
        }
      } else {
        // No account available — ProtectedRoute is responsible for redirecting
        // unauthenticated users to login.  Do NOT call acquireTokenRedirect here:
        // doing so would race with ProtectedRoute's loginRedirect, causing an
        // interaction_in_progress error that resets the ProtectedRoute guard and
        // starts a redirect loop.
        logger.debug(
          '⚠️ Apollo: No account available; skipping token acquisition (ProtectedRoute will handle auth)'
        );
      }

      return { headers };
    });

    return new ApolloClient({
      link: authLink.concat(httpLink),

      cache: new InMemoryCache({
        typePolicies: {
          // Add any cache policies here
        },
      }),

      defaultOptions: {
        watchQuery: {
          errorPolicy: 'all',
        },
        query: {
          errorPolicy: 'all',
        },
      },
    });
    // Recreate client when MSAL auth state changes so active queries refetch
    // with the current account and token state.
  }, [accounts, inProgress, instance, isAuthReady, testApiKey]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default ApolloProviderWithClient;
